import { useState, useEffect, useRef, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Heart,
  SlidersHorizontal,
  MapPin,
  Star,
  X,
  University,
} from "lucide-react";
import Searchbar from "../../Components/Searchbar";
import { motion, AnimatePresence } from "motion/react";
import { useUniversities } from "../../context/UniversityContext";
import { UniversityCard } from "../../Components/Universities/UniversityCard";
import { Navbar } from "../../Components/Navbar";
import { Footer } from "../../Components/Footer";
import { db } from "../../firebase";
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
} from "firebase/firestore";

const TYPES = ["All", "Local", "Overseas"] as const;
type UniversityType = (typeof TYPES)[number];

const getTypeFromParam = (typeParam: string | null): UniversityType => {
  return TYPES.includes(typeParam as UniversityType)
    ? (typeParam as UniversityType)
    : "All";
};

const getTypeFromSearchParams = (searchParams: URLSearchParams): UniversityType => {
  return getTypeFromParam(searchParams.get("type"));
};

export default function ExplorePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const typeParam = searchParams.get("type");
  const [savedSlugs, setSavedSlugs] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");
  const [country, setCountry] = useState("All");
  const [type, setType] = useState<UniversityType>(() =>
    getTypeFromSearchParams(searchParams),
  );
  const [showSaved, setShowSaved] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Typesense state
  const [universities, setUniversities] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const isFetchingRef = useRef(false);

  const toggleSave = (slug: string) =>
    setSavedSlugs((prev) => {
      const next = new Set(prev);
      next.has(slug) ? next.delete(slug) : next.add(slug);
      return next;
    });

  const handleTypeChange = (nextType: UniversityType) => {
    setType(nextType);
    setCountry("All");
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (nextType === "All") {
        next.delete("type");
      } else {
        next.set("type", nextType);
      }
      return next;
    });
  };

  useEffect(() => {
    const nextType = getTypeFromParam(typeParam);
    setType(nextType);
    setCountry("All");
  }, [typeParam]);

  // ----------------------------------------------------------------
  // Firestore fetch logic
  // ----------------------------------------------------------------
  const fetchFromFirestore = async (isInitial: boolean) => {
    if (isFetchingRef.current) return;

    isFetchingRef.current = true;
    if (isInitial) setIsLoading(true);

    try {
      const constraints: any[] = [];

      // Add country filter
      if (type === "Local") {
        constraints.push(where("country", "==", "Malaysia"));
      } else if (type === "Overseas") {
        constraints.push(where("country", "!=", "Malaysia"));
      } else if (country !== "All") {
        constraints.push(where("country", "==", country));
      }

      // Build query (no orderBy to avoid needing composite indexes)
      const q =
        constraints.length > 0
          ? query(collection(db, "university-listing"), ...constraints)
          : query(collection(db, "university-listing"));

      const snapshot = await getDocs(q);
      let docs = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Sort client-side
      docs.sort((a, b) =>
        (a.universityName || "").localeCompare(b.universityName || "")
      );

      // Client-side search filter
      if (search) {
        const searchLower = search.toLowerCase();
        docs = docs.filter(
          (u) =>
            u.universityName?.toLowerCase().includes(searchLower) ||
            u.country?.toLowerCase().includes(searchLower)
        );
      }

      if (isInitial) {
        setUniversities(docs);
      } else {
        setUniversities((prev) => [...prev, ...docs]);
      }

      setTotalCount(docs.length);
      setHasMore(false);
    } catch (err) {
      console.error("Error fetching universities from Firestore:", err);
    } finally {
      isFetchingRef.current = false;
      if (isInitial) setIsLoading(false);
    }
  };

  // Re-fetch whenever search/filter changes
  useEffect(() => {
    fetchFromFirestore(true);
  }, [search, country, type]);

  // Infinite scroll
  useEffect(() => {
    const handleScroll = () => {
      if (
        window.innerHeight + window.scrollY >=
          document.documentElement.scrollHeight - 500 &&
        hasMore &&
        !isFetchingRef.current
      ) {
        fetchFromFirestore(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [hasMore, universities.length]);

  // Derive unique country list from loaded results
  const countries = useMemo(() => {
    const all = [
      "All",
      ...new Set(universities.map((u) => u.country).filter(Boolean)),
    ];
    return all;
  }, [universities]);

  // Apply saved filter client-side (lightweight, no need for Typesense)
  const displayed = useMemo(() => {
    if (showSaved) return universities.filter((u) => savedSlugs.has(u.slug));
    return universities;
  }, [universities, showSaved, savedSlugs]);

  const savedCount = savedSlugs.size;
  const hasFilters = country !== "All" || type !== "All" || showSaved;

  return (
    <div className="bg-gray-50">
      <Navbar />
      <div className="p-4 lg:p-8 max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-16"
        >
          <div className="flex items-center gap-2.5 mb-2">
            <University className="w-6 h-6 text-orange-500" />
            <h1
              className="text-[28px] text-slate-900 tracking-tight"
              style={{ fontWeight: 900 }}
            >
              Our Universities
            </h1>
          </div>
          <p className="text-[14px] text-slate-500" style={{ fontWeight: 400 }}>
            SCS Education can help you explore and compare our list of
            universities. Please note that fees and entry requirements provided
            below are indicative. The final figures will be confirmed with a
            counsellor when you discuss your study plan.
          </p>
        </motion.div>

        {/* Search + filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="mb-6 mt-6"
        >
          <div className="flex gap-3 mb-3">
            {/* Search */}
            <Searchbar
              value={search}
              onChange={setSearch}
              placeholder="Search universities"
            />
            <button
              onClick={() => setFiltersOpen(!filtersOpen)}
              className={`flex items-center gap-2 px-4 py-3 rounded-2xl border text-[13px] transition-all ${filtersOpen || hasFilters ? "bg-violet-50 border-violet-300 text-violet-700" : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"}`}
              style={{ fontWeight: 600 }}
            >
              <SlidersHorizontal className="w-4 h-4" />
              <span className="hidden sm:block">Filters</span>
              {hasFilters && (
                <span className="w-2 h-2 rounded-full bg-violet-600" />
              )}
            </button>
          </div>

          {/* Filters panel */}
          <AnimatePresence>
            {filtersOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 overflow-hidden"
              >
                <div className="grid sm:grid-cols-3 gap-4">
                  <div>
                    <label
                      className="block text-[11px] text-slate-500 mb-2 uppercase tracking-wider"
                      style={{ fontWeight: 700 }}
                    >
                      Country
                    </label>
                    <div className="flex flex-wrap gap-1.5">
                      {countries.map((c) => (
                        <button
                          key={c}
                          onClick={() => setCountry(c)}
                          className={`px-3 py-1.5 rounded-full text-[12px] border transition-all ${country === c ? "bg-violet-50 border-violet-300 text-violet-700" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}
                          style={{ fontWeight: country === c ? 600 : 400 }}
                        >
                          {c}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label
                      className="block text-[11px] text-slate-500 mb-2 uppercase tracking-wider"
                      style={{ fontWeight: 700 }}
                    >
                      Type
                    </label>
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {TYPES.map((t) => (
                        <button
                          key={t}
                          onClick={() => handleTypeChange(t)}
                          className={`px-3 py-1.5 rounded-full text-[12px] border transition-all ${type === t ? "bg-violet-50 border-violet-300 text-violet-700" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}
                          style={{ fontWeight: type === t ? 600 : 400 }}
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                    {/* <button
                      onClick={() => setShowSaved(!showSaved)}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-[12px] border transition-all ${showSaved ? "bg-red-50 border-red-200 text-red-600" : "border-slate-200 text-slate-600"}`}
                      style={{ fontWeight: showSaved ? 600 : 400 }}
                    >
                      <Heart
                        className={`w-3 h-3 ${showSaved ? "fill-red-500 text-red-500" : ""}`}
                      />{" "}
                      Saved only ({savedCount})
                    </button> */}
                  </div>
                </div>
                {hasFilters && (
                  <button
                    onClick={() => {
                      setCountry("All");
                      handleTypeChange("All");
                      setShowSaved(false);
                    }}
                    className="mt-3 text-[12px] text-red-500 hover:text-red-600 flex items-center gap-1"
                    style={{ fontWeight: 600 }}
                  >
                    <X className="w-3 h-3" /> Clear all filters
                  </button>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Quick stats */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="flex items-center gap-4 mb-5 flex-wrap"
        >
          <p className="text-[13px] text-slate-500" style={{ fontWeight: 500 }}>
            Showing{" "}
            <span className="text-slate-900" style={{ fontWeight: 700 }}>
              {isLoading ? "…" : displayed.length}
            </span>{" "}
            {totalCount !== null && !isLoading && (
              <>
                of{" "}
                <span className="text-slate-900" style={{ fontWeight: 700 }}>
                  {totalCount}
                </span>{" "}
              </>
            )}
            universities
          </p>
          <div className="flex items-center gap-2">
            {/* <span className="flex items-center gap-1.5 text-[12px] text-slate-500 font-medium">
              <Heart className="w-3.5 h-3.5 fill-red-400 text-red-400" />{" "}
              {savedCount} saved
            </span> */}
            <span className="flex items-center gap-1.5 text-[12px] text-slate-500 font-medium">
              <MapPin className="w-3.5 h-3.5 text-violet-400" />{" "}
              {[...new Set(displayed.map((u) => u.country))].length} countries
            </span>
          </div>
        </motion.div>

        {/* Loading / Error states */}
        {isLoading && (
          <div className="flex items-center justify-center py-20 text-slate-400 text-[14px]">
            Loading universities…
          </div>
        )}

        {/* Grid */}
        {!isLoading && (
          <motion.div
            layout
            className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5"
          >
            <AnimatePresence>
              {displayed.map((uni, i) => (
                <motion.div
                  key={uni.slug}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ delay: i * 0.04 }}
                >
                  <UniversityCard
                    uni={uni}
                    saved={savedSlugs.has(uni.slug)}
                    onToggleSave={toggleSave}
                    isRecommended={i < 3}
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        )}

        {!isLoading && displayed.length === 0 && (
          <div className="flex items-center justify-center py-20 text-slate-400 text-[14px]">
            No universities match your search.
          </div>
        )}

        {/* Load more indicator */}
        {hasMore && !isLoading && universities.length > 0 && (
          <div className="flex items-center justify-center py-8 text-slate-400 text-[13px]">
            Scroll down to load more…
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
